import appParameters from '@/core/config/parameters';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

export const { MAPBOX_API_KEY } = appParameters;

class MapboxService {
  constructor() {
    /**
     * @see https://github.com/mapbox/mapbox-gl-geocoder/blob/main/API.md#mapboxgeocoder
     */
    this.defaultGeocoderOptions = {
      language: 'pl',
      marker: false,
      flyTo: false, // disable the default flyTo animation (very slow)
      clearAndBlurOnEsc: true, // the geocoder control will clear it's contents and blur when user presses the escape key
      clearOnBlur: false, // the geocoder control will clear its value when the input blurs
      types: 'place',
      countries: 'pl',
      minLength: 4,
      /**
       * A function that specifies how the selected result should be rendered in the search bar.
       * This function should accept a single Carmen GeoJSON object as input and return a string.
       * HTML tags in the output string will not be rendered. Defaults to (item) => item.place_name
       * @param carmenGeoJson
       * @returns {*}
       */
      getItemValue(carmenGeoJson) {
        return carmenGeoJson.text;
      }
    };
  }

  init() {
    this.setAccessToken(MAPBOX_API_KEY);
  }

  apiAvailable() {
    if (typeof mapboxgl === 'undefined') {
      console.error('mapboxgl API missing');
      return false;
    }
    if (!(MAPBOX_API_KEY !== 'undefined' && MAPBOX_API_KEY > '')) {
      console.error('mapboxgl API KEY is empty');
      return false;
    }
    if (!this.webglSupported()) {
      console.error('mapboxgl WebGL unavailable');
      return false;
    }
    return (typeof mapboxgl !== 'undefined') && MAPBOX_API_KEY > '';
  }

  webglSupported() {
    if (typeof mapboxgl === 'undefined' || typeof mapboxgl.supported !== 'function') {
      return false;
    }
    return mapboxgl.supported({ failIfMajorPerformanceCaveat: false });
  }

  setAccessToken(apiKey) {
    mapboxgl.accessToken = apiKey;
  }

  createMap(options = {}) {
    if (!this.apiAvailable()) {
      throw new Error('Mapbox is unavailable: missing API key or WebGL support');
    }
    if (!mapboxgl.accessToken) {
      this.setAccessToken(MAPBOX_API_KEY);
    }
    const mapOptions = $.extend({
      failIfMajorPerformanceCaveat: false
    }, options);
    return new mapboxgl.Map(mapOptions);
  }

  createMarker(options = {}) {
    const markerOptions = $.extend({
    }, options);
    return new mapboxgl.Marker(markerOptions);
  }

  /**
   * @see https://github.com/mapbox/mapbox-gl-geocoder/blob/main/API.md#mapboxgeocoder
   * @param options
   * @returns {MapboxGeocoder}
   */
  createGeocoder(options = {}) {
    this.defaultGeocoderOptions = $.extend(this.defaultGeocoderOptions, {
      accessToken: this.apiAvailable() ? mapboxgl.accessToken : null,
      mapboxgl: this.apiAvailable() ? mapboxgl : null
    });
    const geocoderOptions = $.extend(this.defaultGeocoderOptions, options);
    return new MapboxGeocoder(geocoderOptions);
  }

  LngLat(longitude, latitude) {
    return new mapboxgl.LngLat(
      longitude,
      latitude,
    );
  }
}

export default new MapboxService();
